import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Stack, Button, Chip, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SquareFootIcon from '@mui/icons-material/SquareFoot'
import ApartmentIcon from '@mui/icons-material/Apartment'
import BusinessIcon from '@mui/icons-material/Business'
import PoolIcon from '@mui/icons-material/Pool'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import SecurityIcon from '@mui/icons-material/Security'
import ChildCareIcon from '@mui/icons-material/ChildCare'
import ParkIcon from '@mui/icons-material/Park'
import SpaIcon from '@mui/icons-material/Spa'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import MovieIcon from '@mui/icons-material/Movie'
import SportsTennisIcon from '@mui/icons-material/SportsTennis'
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement'
import WeekendIcon from '@mui/icons-material/Weekend'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import PetsIcon from '@mui/icons-material/Pets'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import PaymentsIcon from '@mui/icons-material/Payments'
import ConstructionIcon from '@mui/icons-material/Construction'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import PlaceIcon from '@mui/icons-material/Place'
import MapIcon from '@mui/icons-material/Map'
import { Navebarehero } from '../Components/Navebarehero'
import { projectsData } from '../Data/projectsData'

// خريطة الأيقونات للـ amenities
const amenityIcons = {
  pool: PoolIcon,
  gym: FitnessCenterIcon,
  parking: LocalParkingIcon,
  security: SecurityIcon,
  kidsArea: ChildCareIcon,
  garden: ParkIcon,
  spa: SpaIcon,
  restaurant: RestaurantIcon,
  mall: ShoppingBagIcon,
  beach: BeachAccessIcon,
  bbq: OutdoorGrillIcon,
  concierge: SupportAgentIcon,
  cinema: MovieIcon,
  playground: ChildCareIcon,
  tennis: SportsTennisIcon,
  jogging: DirectionsRunIcon,
  yoga: SelfImprovementIcon,
  lounge: WeekendIcon,
  businessCenter: BusinessCenterIcon,
  petFriendly: PetsIcon,
}

// عنوان قسم موحّد (نفس ستايل "تفاصيل المشروع" و"المرافق")
const SectionTitle = ({ children }) => {
  const { i18n } = useTranslation()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  return (
    <Typography
      sx={{
        fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
        fontSize: { xs: '1.5rem', md: '2rem' },
        color: '#D9D6CF',
        mb: 3,
      }}
    >
      {children}
    </Typography>
  )
}

export const ProjectDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const lang = isArabic ? 'ar' : 'en'

  const project = projectsData.find((p) => String(p.id) === String(id))

  if (!project) {
    return (
      <Box sx={{ bgcolor: '#2B2B2B', minHeight: '100vh', color: '#D9D6CF' }}>
        <Navebarehero />
        <Stack alignItems="center" justifyContent="center" sx={{ height: '70vh' }}>
          <Typography variant="h4">{t('projectDetails.notFound')}</Typography>
          <Button onClick={() => navigate('/projects')} sx={{ mt: 2, color: '#7A2E3A' }}>
            {t('projectDetails.backToProjects')}
          </Button>
        </Stack>
      </Box>
    )
  }

  const isPaymentCallUs = project.paymentPlan === 'callUs'

  const paymentPlanCards = project.paymentPlan && !isPaymentCallUs
    ? [
        {
          key: 'downPayment',
          icon: PaymentsIcon,
          value: project.paymentPlan.downPayment,
          label: isArabic ? 'الدفعة الأولى' : 'Down Payment',
          sub: isArabic ? 'عند الحجز' : 'On Booking Date',
        },
        {
          key: 'duringConstruction',
          icon: ConstructionIcon,
          value: project.paymentPlan.duringConstruction,
          label: isArabic ? 'أثناء الإنشاء' : 'During Construction',
          sub: isArabic ? 'الدفعات من 1 إلى 5' : '1st to 5th Installment',
        },
        {
          key: 'onHandover',
          icon: VpnKeyIcon,
          value: project.paymentPlan.onHandover,
          label: isArabic ? 'عند التسليم' : 'On Handover',
          sub: isArabic ? 'اكتمال 100%' : '100% Completion',
        },
      ].filter((item) => item.value > 0)
    : []

  return (
    <Box sx={{ bgcolor: '#2B2B2B', minHeight: '100vh' }}>
      <Navebarehero />

      {/* صورة الغلاف */}
      <Box sx={{ position: 'relative', width: '100%', height: { xs: '60vh', md: '70vh' }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={project.image}
          alt={project.name[lang]}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />

        {/* overlay أساسي لتحسين تباين النص */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(43,43,43,0.55) 0%, rgba(43,43,43,0.15) 40%, rgba(43,43,43,0.55) 100%)',
          }}
        />

        {/* ✨ gradient ممتد لأسفل الصورة عشان يمتزج مع خلفية الصفحة */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -1,
            left: 0,
            right: 0,
            height: { xs: '35%', md: '45%' },
            background: 'linear-gradient(180deg, rgba(43,43,43,0) 0%, rgba(43,43,43,0.35) 35%, rgba(43,43,43,0.8) 70%, #2B2B2B 100%)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            px: { xs: 3, md: 8 },
            pb: { xs: 4, md: 6 },
            zIndex: 3,
          }}
        >
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon sx={{ transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
            sx={{
              alignSelf: 'flex-start',
              mb: 2,
              color: '#D9D6CF',
              border: '1px solid rgba(217,214,207,0.3)',
              borderRadius: '999px',
              px: 2,
              '&:hover': { borderColor: '#7A2E3A', bgcolor: 'rgba(122,46,58,0.1)' },
            }}
          >
            {t('projectDetails.back')}
          </Button>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip
              label={project.developer}
              sx={{ bgcolor: 'rgba(122,46,58,0.9)', color: '#fff' }}
            />
            {project.handover && (
              <Chip
                icon={<CalendarMonthIcon sx={{ color: '#D9D6CF !important', fontSize: 18 }} />}
                label={`${isArabic ? 'التسليم' : 'Handover'}: ${project.handover}`}
                sx={{
                  bgcolor: 'rgba(43,43,43,0.75)',
                  color: '#D9D6CF',
                  border: '1px solid rgba(217,214,207,0.25)',
                }}
              />
            )}
          </Stack>

          <Typography
            variant="h1"
            sx={{
              fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
              fontWeight: isArabic ? 700 : 600,
              fontSize: { xs: '2rem', md: '3.5rem' },
              color: '#fff',
            }}
          >
            {project.name[lang]}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mt: 1 }}>
            <LocationOnIcon sx={{ fontSize: 18, color: '#7A2E3A' }} />
            <Typography sx={{ color: 'rgba(217,214,207,0.85)' }}>
              {project.location[lang]}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* التفاصيل */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        sx={{
          maxWidth: 1300,
          mx: 'auto',
          px: { xs: 3, md: 8 },
          py: { xs: 6, md: 8 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 5,
        }}
      >
        {/* العمود الشمال: التفاصيل */}
        <Box>
          <SectionTitle>{t('projectDetails.projectDetails')}</SectionTitle>

          <Stack spacing={3}>
            {/* المطور */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <BusinessIcon sx={{ color: '#7A2E3A', fontSize: 28 }} />
              <Box>
                <Typography sx={{ color: 'rgba(217,214,207,0.6)', fontSize: '0.8rem' }}>
                  {t('projectDetails.developer')}
                </Typography>
                <Typography sx={{ color: '#D9D6CF', fontWeight: 600 }}>
                  {project.developer}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ borderColor: 'rgba(217,214,207,0.12)' }} />

            {/* المساحة */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <SquareFootIcon sx={{ color: '#7A2E3A', fontSize: 28 }} />
              <Box>
                <Typography sx={{ color: 'rgba(217,214,207,0.6)', fontSize: '0.8rem' }}>
                  {t('projectDetails.area')}
                </Typography>
                <Typography sx={{ color: '#D9D6CF', fontWeight: 600 }}>
                  {project.sqft ? `${project.sqft.toLocaleString()} ${t('projectDetails.sqft')}` : '—'}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ borderColor: 'rgba(217,214,207,0.12)' }} />

            {/* وقت التسليم */}
            {project.handover && (
              <>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CalendarMonthIcon sx={{ color: '#7A2E3A', fontSize: 28 }} />
                  <Box>
                    <Typography sx={{ color: 'rgba(217,214,207,0.6)', fontSize: '0.8rem' }}>
                      {isArabic ? 'موعد التسليم' : 'Handover'}
                    </Typography>
                    <Typography sx={{ color: '#D9D6CF', fontWeight: 600 }}>
                      {project.handover}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ borderColor: 'rgba(217,214,207,0.12)' }} />
              </>
            )}

            {/* الأنواع */}
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <ApartmentIcon sx={{ color: '#7A2E3A', fontSize: 28 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: 'rgba(217,214,207,0.6)', fontSize: '0.8rem', mb: 1 }}>
                  {t('projectDetails.unitTypes')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {(project.types || []).map((type) => (
                    <Chip
                      key={type}
                      label={t(`unitTypes.${type}`)}
                      sx={{
                        bgcolor: 'rgba(217,214,207,0.08)',
                        color: '#D9D6CF',
                        border: '1px solid rgba(217,214,207,0.2)',
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Stack>

          {/* المرافق - Amenities */}
          {project.amenities?.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <SectionTitle>{t('projectDetails.amenities')}</SectionTitle>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(3, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: 2,
                }}
              >
                {project.amenities.map((key) => {
                  const Icon = amenityIcons[key] || SpaIcon
                  return (
                    <Stack
                      key={key}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        border: '1px solid rgba(217,214,207,0.12)',
                        bgcolor: 'rgba(217,214,207,0.03)',
                        transition: 'border-color 0.3s',
                        '&:hover': { borderColor: 'rgba(122,46,58,0.5)' },
                      }}
                    >
                      <Icon sx={{ color: '#7A2E3A', fontSize: 24 }} />
                      <Typography sx={{ color: '#D9D6CF', fontSize: '0.9rem' }}>
                        {t(`amenitiesList.${key}`)}
                      </Typography>
                    </Stack>
                  )
                })}
              </Box>
            </Box>
          )}

          {/* خطة الدفع - Payment Plan */}
          {(paymentPlanCards.length > 0 || isPaymentCallUs) && (
            <Box sx={{ mt: 6 }}>
              <SectionTitle>{isArabic ? 'خطة الدفع' : 'Payment Plan'}</SectionTitle>

              {isPaymentCallUs ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    border: '1px solid rgba(217,214,207,0.15)',
                    bgcolor: 'rgba(217,214,207,0.03)',
                  }}
                >
                  <PaymentsIcon sx={{ color: '#7A2E3A', fontSize: 30 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#D9D6CF', fontWeight: 600 }}>
                      {isArabic
                        ? 'تفاصيل خطة الدفع غير متاحة حاليًا — تواصل معنا لمعرفة أحدث العروض.'
                        : "Payment plan details aren't published yet — contact us for the latest offers."}
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => navigate('/contact')}
                    sx={{
                      borderRadius: '999px',
                      px: 3,
                      py: 1,
                      bgcolor: '#7A2E3A',
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: '#8f3743' },
                    }}
                  >
                    {isArabic ? 'اتصل بنا' : 'Call Us'}
                  </Button>
                </Stack>
              ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                {paymentPlanCards.map((item) => {
                  const Icon = item.icon
                  return (
                    <Stack
                      key={item.key}
                      alignItems="center"
                      spacing={1}
                      sx={{
                        p: 3,
                        borderRadius: '16px',
                        border: '1px solid rgba(217,214,207,0.15)',
                        bgcolor: 'rgba(217,214,207,0.03)',
                        textAlign: 'center',
                        transition: 'border-color 0.3s',
                        '&:hover': { borderColor: 'rgba(122,46,58,0.5)' },
                      }}
                    >
                      <Icon sx={{ color: '#7A2E3A', fontSize: 30 }} />
                      <Typography
                        sx={{
                          fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
                          fontSize: '1.8rem',
                          fontWeight: 700,
                          color: '#fff',
                        }}
                      >
                        {item.value}%
                      </Typography>
                      <Typography sx={{ color: '#D9D6CF', fontWeight: 600, fontSize: '0.9rem' }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ color: 'rgba(217,214,207,0.55)', fontSize: '0.78rem' }}>
                        {item.sub}
                      </Typography>
                    </Stack>
                  )
                })}
              </Box>
              )}
            </Box>
          )}

          {/* موقع المشروع - Location Map */}
          {project.nearbyPlaces?.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <SectionTitle>{isArabic ? 'الموقع والمناطق القريبة' : 'Location Map'}</SectionTitle>

              <Box
                sx={{
                  borderRadius: '16px',
                  border: '1px solid rgba(217,214,207,0.15)',
                  overflow: 'hidden',
                  mb: 2.5,
                }}
              >
                <Box
                  component="iframe"
                  title="project-location-map"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(project.location.en)}&output=embed`}
                  sx={{ width: '100%', height: 280, border: 0, display: 'block', filter: 'grayscale(0.15)' }}
                  loading="lazy"
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(4, 1fr)',
                  },
                  gap: 2,
                }}
              >
                {project.nearbyPlaces.map((place, idx) => (
                  <Stack
                    key={idx}
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: '1px solid rgba(217,214,207,0.12)',
                      bgcolor: 'rgba(217,214,207,0.03)',
                      textAlign: 'center',
                    }}
                  >
                    <PlaceIcon sx={{ color: '#7A2E3A', fontSize: 24 }} />
                    <Typography sx={{ color: '#D9D6CF', fontSize: '0.85rem', fontWeight: 600 }}>
                      {place.name[lang]}
                    </Typography>
                    <Typography sx={{ color: 'rgba(217,214,207,0.6)', fontSize: '0.78rem' }}>
                      {place.time}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Box>
          )}

          {/* المخططات - Floor Plan */}
          {project.floorPlan?.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <SectionTitle>{isArabic ? 'مخطط الطوابق' : 'Floor Plan'}</SectionTitle>

              <TableContainer
                component={Paper}
                sx={{
                  bgcolor: 'rgba(217,214,207,0.03)',
                  border: '1px solid rgba(217,214,207,0.15)',
                  borderRadius: '16px',
                  boxShadow: 'none',
                }}
              >
                <Table sx={{ minWidth: 560 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        isArabic ? 'الفئة' : 'Category',
                        isArabic ? 'نوع الوحدة' : 'Unit Type',
                        isArabic ? 'تفاصيل الطابق' : 'Floor Details',
                        isArabic ? 'المساحات' : 'Sizes',
                        isArabic ? 'النوع' : 'Type',
                      ].map((head) => (
                        <TableCell
                          key={head}
                          sx={{
                            color: '#D9D6CF',
                            fontWeight: 700,
                            borderColor: 'rgba(217,214,207,0.15)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {project.floorPlan.map((row, idx) => (
                      <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ color: 'rgba(217,214,207,0.85)', borderColor: 'rgba(217,214,207,0.1)' }}>
                          {row.category}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(217,214,207,0.85)', borderColor: 'rgba(217,214,207,0.1)' }}>
                          {row.unitType}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(217,214,207,0.85)', borderColor: 'rgba(217,214,207,0.1)' }}>
                          {row.floorDetails}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(217,214,207,0.85)', borderColor: 'rgba(217,214,207,0.1)', whiteSpace: 'nowrap' }}>
                          {row.sizes}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(217,214,207,0.85)', borderColor: 'rgba(217,214,207,0.1)' }}>
                          {row.type}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>

        {/* العمود اليمين: السعر + CTA */}
        <Box
          sx={{
            alignSelf: 'flex-start',
            position: { md: 'sticky' },
            top: { md: 100 },
            border: '1px solid rgba(217,214,207,0.15)',
            borderRadius: '20px',
            bgcolor: 'rgba(217,214,207,0.03)',
            backdropFilter: 'blur(12px)',
            p: 4,
          }}
        >
          <Typography sx={{ color: 'rgba(217,214,207,0.6)', fontSize: '0.85rem' }}>
            {t('allProjects.startingFrom')}
          </Typography>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
              fontWeight: 700,
              fontSize: { xs: '1.8rem', md: '2.2rem' },
              mt: 0.5,
              mb: project.handover ? 1 : 3,
            }}
          >
            {project.price.toLocaleString()} {isArabic ? 'درهم' : 'AED'}
          </Typography>

          {project.handover && (
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 3 }}>
              <CalendarMonthIcon sx={{ color: '#7A2E3A', fontSize: 18 }} />
              <Typography sx={{ color: 'rgba(217,214,207,0.7)', fontSize: '0.85rem' }}>
                {isArabic ? 'التسليم' : 'Handover'}: {project.handover}
              </Typography>
            </Stack>
          )}

          <Button
            fullWidth
            onClick={() => navigate('/contact')}
            endIcon={<ArrowForwardIcon sx={{ transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              bgcolor: '#7A2E3A',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#8f3743' },
            }}
          >
            {t('projectDetails.contactUs')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}